import { createClient } from "@supabase/supabase-js";
import { seed } from "./seed";
import { uid, buildOrder, nextStatusAllowed, paidStatuses } from "./domain";
export const config = import.meta.env.DEV && import.meta.env.VITE_DEMO === "true" ? {} : window.DILO_CONFIG || {};
export const demo = !config.supabaseUrl && !config.supabaseAnonKey;
if (
  !demo &&
  (!/^https:\/\/[a-zA-Z0-9.-]+\.supabase\.co$/.test(config.supabaseUrl) ||
    !config.supabaseAnonKey)
)
  throw new Error("Revisa los dos valores de Supabase en config.js.");
export const supabase = demo
  ? null
  : createClient(config.supabaseUrl, config.supabaseAnonKey);
const key = "dilo-demo-v1";
const read = () => JSON.parse(localStorage.getItem(key) || "null") || seed();
const write = (data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    throw new Error(
      "Se llenó el espacio de la demostración. Usa archivos más pequeños o configura Supabase.",
    );
  }
  window.dispatchEvent(new Event("dilo-change"));
};
export async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(error.message);
  return data;
}
async function query(q) {
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data;
}
async function all(table, filter) {
  let result = [],
    offset = 0;
  while (true) {
    let q = supabase
      .from(table)
      .select(
        table === "mail_outbox"
          ? "id,store_id,order_id,event,status,attempts,error,created_at,updated_at"
          : "*",
      )
      .order("id")
      .range(offset, offset + 999);
    q = filter(q);
    const page = await query(q);
    result.push(...page);
    if (page.length < 1000) return result;
    offset += 1000;
  }
}
export const api = {
  async productReviews(productId) {
    if (demo) return { count: 0, average: 0, items: [] };
    return rpc("product_reviews", { p_product: productId });
  },
  async load(admin = false) {
    if (demo) {
      const d = read();
      if (!localStorage.getItem(key)) write(d);
      return d;
    }
    const store = await query(
      supabase
        .from("stores")
        .select("*")
        .eq("slug", config.storeSlug || "dilo-shop")
        .maybeSingle(),
    );
    if (!store)
      return {
        store: null,
        products: [],
        categories: [],
        variants: [],
        orders: [],
        members: [],
        movements: [],
        events: [],
        visits: 0,
      };
    const names = [
      "categories",
      "products",
      "variants",
      ...(admin
        ? [
            "orders",
            "order_items",
            "members",
            "movements",
            "order_events",
            "mail_outbox",
          ]
        : []),
    ];
    const values = await Promise.all(
      names.map((table) => all(table, (q) => q.eq("store_id", store.id))),
    );
    const data = Object.fromEntries(names.map((name, i) => [name, values[i]]));
    return {
      ...data,
      store,
      products: data.products.sort((a, b) => a.sort_order - b.sort_order),
      categories: data.categories.sort((a, b) => a.sort_order - b.sort_order),
      orders: (data.orders || [])
        .map((o) => ({
          ...o,
          items: data.order_items.filter((i) => i.order_id === o.id),
        }))
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
      members: data.members || [],
      movements: data.movements || [],
      events: data.order_events || [],
      visits: admin ? await rpc("visit_count", { p_store: store.id }) : 0,
    };
  },
  async saveProduct(product, variants) {
    if (
      !product.name.trim() ||
      !product.sku.trim() ||
      product.price < 0 ||
      !Number.isFinite(product.price)
    )
      throw new Error("Revisa el nombre, código y precio.");
    if (
      [product.compare_price, product.wholesale_price].some(
        (n) => !Number.isFinite(n) || n < 0,
      ) ||
      !Number.isInteger(product.wholesale_min) ||
      product.wholesale_min < 1
    )
      throw new Error("Revisa los precios y la cantidad mayorista.");
    if (
      !variants.length ||
      variants.some(
        (v) => !v.sku || !v.color || !Number.isInteger(v.stock) || v.stock < 0,
      )
    )
      throw new Error("Cada color necesita código y stock válido.");
    if (demo) {
      const d = read();
      if (d.products.some((p) => p.sku === product.sku && p.id !== product.id))
        throw new Error("Ese código de producto ya existe.");
      if (
        new Set(variants.map((v) => v.sku)).size !== variants.length ||
        variants.some((v) =>
          d.variants.some((x) => x.sku === v.sku && x.id !== v.id),
        )
      )
        throw new Error("El código de variante ya existe.");
      const p = { ...product, id: product.id || uid() };
      const idx = d.products.findIndex((x) => x.id === p.id);
      if (idx < 0) d.products.push(p);
      else d.products[idx] = p;
      for (const variant of variants) {
        const v = { ...variant, id: variant.id || uid(), product_id: p.id };
        const old = d.variants.find((x) => x.id === v.id);
        if (old && v.stock !== old.stock)
          throw new Error(
            "Actualiza las cantidades desde Inventario para mantener el historial.",
          );
        if (!old) {
          d.variants.push(v);
          d.movements.unshift({
            id: uid(),
            variant_id: v.id,
            delta: v.stock,
            stock_after: v.stock,
            reason: "Stock inicial",
            created_at: new Date().toISOString(),
          });
        } else Object.assign(old, v);
      }
      write(d);
      return p.id;
    }
    return rpc("save_product", { p_product: product, p_variants: variants });
  },
  async saveCategory(category) {
    if (demo) {
      const d = read();
      const c = { ...category, id: category.id || uid() };
      if (c.id === c.parent_id)
        throw new Error("Una categoría no puede contenerse a sí misma.");
      const i = d.categories.findIndex((x) => x.id === c.id);
      i < 0 ? d.categories.push(c) : (d.categories[i] = c);
      write(d);
      return;
    }
    await query(supabase.from("categories").upsert(category));
  },
  async deleteCategory(id) {
    if (demo) {
      const d = read();
      if (
        d.products.some((p) => p.category_id === id) ||
        d.categories.some((c) => c.parent_id === id)
      )
        throw new Error("Primero mueve los productos y subcategorías.");
      d.categories = d.categories.filter((c) => c.id !== id);
      write(d);
      return;
    }
    await query(supabase.from("categories").delete().eq("id", id));
  },
  async saveStore(store) {
    if (demo) {
      const d = read();
      d.store = store;
      write(d);
      return;
    }
    const { id, slug, created_at, ...settings } = store;
    await query(supabase.from("stores").update(settings).eq("id", id));
  },
  async reorder(ids) {
    if (demo) {
      const d = read();
      ids.forEach((id, i) => {
        d.products.find((p) => p.id === id).sort_order = i;
      });
      write(d);
      return;
    }
    await rpc("reorder_products", { p_ids: ids });
  },
  async adjustStock(id, delta, reason) {
    if (!Number.isInteger(delta) || !delta || !reason.trim())
      throw new Error("Indica una cantidad entera y el motivo.");
    if (demo) {
      const d = read(),
        v = d.variants.find((v) => v.id === id);
      if (v.stock + delta < 0)
        throw new Error("El stock no puede ser negativo.");
      v.stock += delta;
      d.movements.unshift({
        id: uid(),
        variant_id: id,
        delta,
        stock_after: v.stock,
        reason,
        created_at: new Date().toISOString(),
      });
      write(d);
      return;
    }
    await rpc("adjust_stock", {
      p_variant: id,
      p_delta: delta,
      p_reason: reason,
    });
  },
  async createOrder(payload, manual = false) {
    if (demo) {
      const d = read(),
        prev = d.orders.find((o) => o.request_key === payload.request_key);
      if (prev) return prev;
      const o = buildOrder(d, payload, manual ? "MANUAL" : "WEB");
      for (const item of o.items) {
        const v = d.variants.find((v) => v.id === item.variant_id);
        v.stock -= item.quantity;
        d.movements.unshift({
          id: uid(),
          variant_id: v.id,
          delta: -item.quantity,
          stock_after: v.stock,
          reason: `Pedido ${o.number}`,
          order_id: o.id,
          created_at: o.created_at,
        });
      }
      d.orders.unshift(o);
      write(d);
      return o;
    }
    if (manual)
      return rpc("place_order", {
        p_slug: config.storeSlug || "dilo-shop",
        p_payload: payload,
      });
    const r = await fetch("/api/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const body = await r.json();
    if (!r.ok) throw new Error(body.error || "No se pudo crear el pedido.");
    return body;
  },
  async setStatus(id, status) {
    if (demo) {
      const d = read(),
        o = d.orders.find((o) => o.id === id);
      if (!nextStatusAllowed(o.status, status))
        throw new Error("No se puede retroceder este pedido.");
      if (o.status === status) return;
      if (status === "Cancelado")
        for (const i of o.items) {
          const v = d.variants.find((v) => v.id === i.variant_id);
          v.stock += i.quantity;
          d.movements.unshift({
            id: uid(),
            variant_id: v.id,
            delta: i.quantity,
            stock_after: v.stock,
            reason: `Cancelación ${o.number}`,
            order_id: o.id,
            created_at: new Date().toISOString(),
          });
        }
      if (paidStatuses.includes(status) && !o.paid_at)
        o.paid_at = new Date().toISOString();
      d.events.unshift({
        id: uid(),
        order_id: id,
        previous_status: o.status,
        status,
        created_at: new Date().toISOString(),
      });
      o.status = status;
      write(d);
      return;
    }
    await rpc("set_order_status", { p_order: id, p_status: status });
  },
  async assign(id, member) {
    if (demo) {
      const d = read();
      d.orders.find((o) => o.id === id).collaborator_id = member || null;
      write(d);
      return;
    }
    await rpc("assign_order", { p_order: id, p_member: member || null });
  },
  async upload(file, storeId, privateFile = false) {
    const allowed = privateFile
      ? ["image/png", "image/jpeg", "image/webp", "application/pdf"]
      : [
          "image/png",
          "image/jpeg",
          "image/webp",
          "image/gif",
          "video/mp4",
          "video/webm",
        ];
    if (!allowed.includes(file.type))
      throw new Error(
        "Formato no admitido. Usa JPG, PNG, WebP, MP4 o WebM; PDF solo para comprobantes.",
      );
    if (file.size > (file.type.startsWith("video/") ? 40 : 10) * 1024 * 1024)
      throw new Error("Máximo: 10 MB por imagen y 40 MB por video.");
    if (demo) {
      if (file.size > 2 * 1024 * 1024)
        throw new Error(
          "En demostración usa archivos de hasta 2 MB; Supabase admite archivos mayores.",
        );
      return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
    }
    const ext = file.name
      .split(".")
      .pop()
      .replace(/[^a-z0-9]/gi, "");
    const path = `${storeId}/${uid()}.${ext}`,
      bucket = privateFile ? "receipts" : "media";
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { contentType: file.type, upsert: false });
    if (error) throw new Error(error.message);
    return privateFile
      ? path
      : supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  },
  async attachReceipt(orderId, path) {
    if (demo) {
      const d = read();
      d.orders.find((o) => o.id === orderId).receipt_url = path;
      write(d);
      return;
    }
    await rpc("attach_receipt", { p_order: orderId, p_path: path });
  },
  async receiptUrl(path) {
    if (demo) return path;
    const { data, error } = await supabase.storage
      .from("receipts")
      .createSignedUrl(path, 120);
    if (error) throw error;
    return data.signedUrl;
  },
  async visit() {
    if (demo) {
      const d = read();
      d.visits++;
      write(d);
    } else
      await rpc("track_visit", { p_slug: config.storeSlug || "dilo-shop" });
  },
  async invite(store, email, role) {
    if (demo)
      throw new Error("Las invitaciones se activan al conectar Supabase.");
    return rpc("invite_member", {
      p_store: store,
      p_email: email,
      p_role: role,
    });
  },
  async removeMember(store, user) {
    if (demo)
      throw new Error("Esta cuenta es el propietario de la demostración.");
    return rpc("remove_member", { p_store: store, p_user: user });
  },
  resetDemo() {
    localStorage.removeItem(key);
    write(seed());
  },
};
